import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, debounceTime, exhaustMap, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { CallerService } from 'src/app/services/caller.service';
import {
  loadAllItems,
  loadAllItemsFailure,
  loadAllItemsSuccess,
  loadSavedSales,
  loadSavedAccommodations,
  newMessage,
  profileUpdateError,
  profileUpdateSuccess,
  savedSalesLoaded,
  savedAccommodationsLoaded,
  updateCorpMemberProfile,
} from '../actions/corp-member.actions';
import { selectFeatureFeed } from '../selectors/corp.selectors';
import { AppState } from '../app.state';
import { Router } from '@angular/router';

// Matches the backend's own /all-items cache TTL - no point refetching
// more often than the source data actually changes.
const FEED_CACHE_TTL_MS = 45 * 1000;

@Injectable()
export class CorpMemberEffects {

  // @todo: can this be improved?
  updateProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCorpMemberProfile),
      debounceTime(500),
      switchMap((data) => this.callerService.updateProfile(data).pipe(
        tap((res) => console.log('what tapppp?', res)),
        map(res => profileUpdateSuccess({data: res.body})),
        catchError(() => of(profileUpdateError()))
      ))
    )
  );

  /**
   * Components dispatch loadAllItems() unconditionally on mount; this effect
   * decides whether that actually means a network call. Cache-first: if the
   * feed was fetched within FEED_CACHE_TTL_MS, the dispatch is a no-op.
   * exhaustMap also collapses concurrent dispatches (e.g. multiple feed
   * pages mounting in quick succession) into a single in-flight request.
   */
  loadAllItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllItems),
      withLatestFrom(this.store.select(selectFeatureFeed)),
      filter(([, feed]) => !feed.lastFetchedAt || Date.now() - feed.lastFetchedAt > FEED_CACHE_TTL_MS),
      exhaustMap(() => this.callerService.getAllItems().pipe(
        map((res: any) => {
          const body = res.body ?? res;
          return loadAllItemsSuccess({
            sales: body?.sales ?? [],
            accommodations: body?.accommodations ?? [],
            fetchedAt: Date.now(),
          });
        }),
        catchError((error) => of(loadAllItemsFailure({ error })))
      ))
    )
  );

  /**
   * Once per login: which sales this corp member saved. Nothing when logged out, or if they're
   * already loaded (logout clears them). A failure leaves them unloaded, so the next page tries again.
   */
  loadSavedSales$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSavedSales),
      withLatestFrom(this.store.select(selectFeatureFeed)),
      filter(([, feed]) => this.callerService.isLoggedIn && !feed.savedSaleIds),
      exhaustMap(() => this.callerService.getSavedSaleIds().pipe(
        map((res) => savedSalesLoaded({ ids: Array.isArray(res.body?.data) ? res.body!.data : [] })),
        catchError(() => EMPTY)
      ))
    )
  );

  /** The same for houses: once per login, nothing when logged out, a failure lets the next card try again. */
  loadSavedAccommodations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSavedAccommodations),
      withLatestFrom(this.store.select(selectFeatureFeed)),
      filter(([, feed]) => this.callerService.isLoggedIn && !feed.savedAccommodationIds),
      exhaustMap(() => this.callerService.getSavedAccommodationIds().pipe(
        map((res) => savedAccommodationsLoaded({ ids: Array.isArray(res.body?.data) ? res.body!.data : [] })),
        catchError(() => EMPTY)
      ))
    )
  );

  newChatMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(newMessage),
      debounceTime(100),
      tap((data) => {
        // after a new message action is gotten, navigate to the messages page
        const queryParams: any = { r: data?.recipient_id };
        if (data?.source) {
          queryParams.from = data.source;
        }
        if (data?.draft) {
          queryParams.draft = data.draft;
        }
        if (data?.listing) {
          queryParams.about = `${data.listing.listing_type}:${data.listing.listing_slug}`;
        }
        this.router.navigate(['/messages'], { queryParams })
      }),
    ),
    { dispatch: false } // No need to dispatch any action after this effect
  );

  constructor(private actions$: Actions,
    private router: Router,
    private store: Store<AppState>,
    private callerService: CallerService) {}
}
